import {Component, TemplateRef, ViewChild} from '@angular/core';
import {SubscribedComponent} from "../subscribed.component";
import {TranslateService} from "@ngx-translate/core";
import {ColDef, ColType, Delete} from "../interfaces/colDef";
import {activeFilterKeys, DualAxisComponent, DualAxisDelegate, DualAxisEvent, Editable} from "../dual-axis/dual-axis.component";
import {Archive, ArchiveType} from "../interfaces/archive";
import {Subject} from 'rxjs';
import {Paged} from '../interfaces/paged';
import {TreeNode} from '../interfaces/treenode';
import {TableOptions} from '../settings/table-options/table-options.component';
import {ArchiveService} from '../shared/archive.service';
import {SettingsService} from "../shared/settings.service";
import {ProjectComponent} from "../project/project.component";
import {ClientComponent} from "../clients/client.component";
import {UserComponent} from "../user/user.component";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {NbDialogService} from "@nebular/theme";
import {ActivatedRoute, Router} from "@angular/router";
import moment from "moment";
import {ClientService} from "../shared/client.service";
import {UsersService} from "../shared/users.service";
import {ProjectService} from "../shared/project.service";
import {ClientStatus} from "../interfaces/client";
import {ProjectStatus} from "../interfaces/project";
import {UserStatus} from "../interfaces/user";
import {NotifierService} from "../shared/notifier.service";
import {AuthService} from "../shared/auth.service";
import {DirectoryService} from "../shared/directory.service";
import {DirectoryStatus} from "../interfaces/directory";

@Component({
    selector: 'bst-archive-bin',
    templateUrl: './archive-bin.component.html',
    styleUrls: ['./archive-bin.component.scss'],
    standalone: false
})
export class ArchiveBinComponent extends SubscribedComponent implements DualAxisDelegate<Archive> {

  ArchiveType = ArchiveType;
  type: ArchiveType = null;
  types: any[] = Object.values(ArchiveType);

  constructor(private translate: TranslateService,
              private settings: SettingsService,
              private dialogService: NbDialogService,
              private router: Router,
              private route: ActivatedRoute,
              private clientService: ClientService,
              private userService: UsersService,
              private projectService: ProjectService,
              private directoryService: DirectoryService,
              private notifier: NotifierService,
              private auth:AuthService,
              private archive: ArchiveService) {
    super();
  }

  @ViewChild('cellTemplate', {static: true, read: TemplateRef}) cellTemplateOutlet: TemplateRef<any>;
  @ViewChild('dualAxisComponent', {static: true}) dualAxis: DualAxisComponent<Archive>;

  public get filterCount(): number { return activeFilterKeys(this.options?.filter).length; }

  count: number = null;
  data: TreeNode<Archive>[];
  stickyColumnsLeft: ColDef[];
  standardColumns: ColDef[] = [
    {
      name: 'type',
      title: 'Type',
      type: ColType.SELECT,
      filter: false,
      sort: false,
      required: true,
      disabled: true
    },
    {
      name: 'name',
      title: 'Name',
      type: ColType.STRING,
      filter: true,
      sort: true,
      required: true,
      disabled: true
    },
    {
      name: 'created_at',
      title: 'Created on',
      type: ColType.DATE,
      filter: true,
      sort: true,
      required: false
    },
    {
      name: 'created_by',
      title: 'Created by',
      type: ColType.STRING,
      filter: true,
      sort: true,
      required: false
    },
    {
      name: 'archived_at',
      title: 'Archived on',
      type: ColType.DATE,
      filter: true,
      sort: true,
      required: false
    },
    {
      name: 'archived_by',
      title: 'Archived by',
      type: ColType.STRING,
      filter: true,
      sort: true,
      required: false
    }
  ];
  stickyColumnsRight: ColDef[];
  otherColumns?: ColDef[];
  options: TableOptions = {
    sorts: [],
    display: this.settings.mergeTableColumns('table_archived', this.standardColumns),
    filter: null};
  // sorts?: { col: string; dir: number; }[];
  optionsChange?: (newvalue: any) => boolean;
  events?: Subject<DualAxisEvent>;
  sorter?: string | ((a: TreeNode<Archive>, b: TreeNode<Archive>) => number);
  editable?: Editable<Archive> = {
    canView: (row: TreeNode<Archive>) => {
      return [ArchiveType.Project, ArchiveType.User, ArchiveType.Client].includes(row.data.type);
    },
    canDelete: (row: TreeNode<Archive>) => {
      return this.auth.isAdmin;
    },
    canUnarchive: (row: TreeNode<Archive>) => {
      return this.auth.isAdmin || (this.auth.isStaffer && [ArchiveType.Project, ArchiveType.User, ArchiveType.Client].includes(row.data.type));
    },
    unarchive: async (row: TreeNode<Archive>) => {
      let newitem:{status: number} = null;
      switch (row.data.type){
        case ArchiveType.Client:
          newitem = await this.clientService.changeClientStatus(row.data.id, ClientStatus.ACTIVE);
          break;
        case ArchiveType.Project:
          newitem = await this.projectService.changeProjectStatus(row.data.id, ProjectStatus.ACTIVE);
          break;
        case ArchiveType.User:
          let useritem = await this.userService.changeUserStatus(row.data.id, UserStatus.ACTIVE);
          if(useritem && useritem.user_to_client?.status > 0){
            newitem = {status: useritem.user_to_client?.status};
          }
          break;
        case ArchiveType.Seniorities:
        case ArchiveType.Degrees:
        case ArchiveType.ProjectRoles:
        case ArchiveType.Questions:
        case ArchiveType.Departments:
        case ArchiveType.Development:
        case ArchiveType.Skills:
        case ArchiveType.SkillCategories:
        case ArchiveType.Certifications:
          newitem = await this.directoryService.changeDirectoryStatus(this.getDirectoryName(row.data.type), row.data.id, DirectoryStatus.ACTIVE);
          break;
      }

      if(newitem?.status > 0){
        this.removeItem(row);
      }
    },
    delete: async (row: TreeNode<Archive>) => {
      if(await this.notifier.confirm('deletecheck', row.data.name, 'deleteforever', 'danger')){
        let did: Delete = null;
        switch (row.data.type){
          case ArchiveType.User:
            did = await this.userService.deleteUserClient(row.data.id);
            break;
          case ArchiveType.Project:
            did = await this.projectService.deleteProject(row.data.id, this.translate.currentLang);
            break;
          case ArchiveType.Client:
            did = await this.clientService.deleteClient(row.data.id);
            break;
          case ArchiveType.Seniorities:
          case ArchiveType.Degrees:
          case ArchiveType.ProjectRoles:
          case ArchiveType.Questions:
          case ArchiveType.Departments:
          case ArchiveType.Development:
          case ArchiveType.Skills:
          case ArchiveType.SkillCategories:
          case ArchiveType.Certifications:
            did = await this.directoryService.deleteDirectory(this.getDirectoryName(row.data.type), row.data.id);
            break;
        }

        if(did && did.deleted) {
          this.removeItem(row);
        }
      }
    },
    view: (row: TreeNode<Archive>) => {
      const url = row.data.type.toString() + '/' + row.data.id;
      return this.router.navigate([url]);
    }
  };

  private getDirectoryName(type: ArchiveType): string {
    switch (type) {
      case ArchiveType.Skills:
        return 'skills';
      case ArchiveType.SkillCategories:
        return 'skill-categories';
      case ArchiveType.Certifications:
        return 'certifications';
      default:
        return type.toString();
    }
  }

  private removeItem(row: TreeNode<Archive>){
    const index = this.data.indexOf(row);
    if(index !== -1){
      this.data.splice(index, 1);
    }
  }

  ngOnInit() {
    super.ngOnInit();

    this.subs.push(this.route.paramMap.subscribe(async (o) => {
      const type = <ArchiveType>(o.get('type'));
      if(this.types.includes(type)){
        this.type = type;
      }
      else{
        this.type = null;
      }

      if(this.dualAxis){
        await this.dualAxis.reset();
      }
    }));
  }

  async paged(page: number): Promise<Paged<Archive>>{
    this.loading.next(true);
    const filters = this.options.filter;

    try {
      const items = await this.archive.getArchives(this.type, page, filters);

      if (page <= 1) {
        this.data = [];
      }

      this.loading.next(false);
      if (items && items.data) {
        this.count = items.total;
        this.data = this.data.concat(items.data.map((o) => ArchiveBinComponent.transform(o)));
        return items;
      }
    } catch(err){
      this.loading.next(false);
      this.type = null;
    }
  }

  private static transform(item: Archive): TreeNode<Archive>{
    if(item.archived_at){
      item.archived_at = moment.utc(item.archived_at).toDate();
    }
    if(item.created_at){
      item.created_at = moment.utc(item.created_at).toDate();
    }
    return <TreeNode<Archive>>{
      data: item,
      expanded: false,
      children: []
    };
  }

  preview(item: Archive) {
    let comp;
    switch (item.type) {
      case ArchiveType.Project:
        comp = ProjectComponent;
        break;
      case ArchiveType.Client:
        comp = ClientComponent;
        break;
      default:
        comp = UserComponent;
        break;
    }

    const open = this.dialogService.open<PopupDialogComponent<typeof comp>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        comp: comp,
        componentContext: {
          height: '300px',
          couldEdit: false,
          couldArchive: false,
          couldAssign: false,
          id: item.id
        }
      }
    });

    const suba = open.onClose.subscribe(async (res: typeof comp) => {
      suba.unsubscribe();
      if(res.touched){
        return await this.dualAxis.reset();
      }
    });
  }

  async changeTo(type: ArchiveType) {
    await this.router.navigate(['/bin' + (type ? '/' + type : '')]);
  }

  ago(item) {
    if(item) {
      const m = moment(item);
      return Math.ceil(moment().diff(m, 'days', true));
    }
    return null;
  }

  get headerTitle(): string | { title: string; translate: boolean | Object } {
    return 'Archive Bin';
  }
}
